/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import clsx from 'clsx';
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';

import { calculateTimeLeft, checkCampaignStatus } from '../../utils/OfferUtils';
import { shouldUseMockData } from '../../utils/CommonUtils';

import { OnOffContainerProps } from './OnOffContainer.types';

export const OnOffContainerWrapper = (props: any) => {
  const { isAuthorMode, model, mbeData, isPreview, mbeModelData } = props;
  const mbeDynamicWorkflow = props?.mbeDynamicWorkflow;
  const { variation } = model;
  const pathParam = useSearchParams();
  const cycleState = pathParam?.get('cycle');
  const mbeOffersData = mbeDynamicWorkflow ? mbeData?.responseObject?.offers?.mbeOfferById?.offers : mbeModelData;
  const [offersDataMock, setoffersDataMock] = useState<any>({});
  const sourceMBEData = !isAuthorMode ? mbeOffersData : offersDataMock;
  const campaignStartDate =
    (mbeDynamicWorkflow ? sourceMBEData?.onCycleStartDate : sourceMBEData?.campaignStartDate) || '';
  const campaignEndDate = (mbeDynamicWorkflow ? sourceMBEData?.onCycleEndDate : sourceMBEData?.campaignEndDate) || '';
  const [utilityState, setUtilityState] = useState<string>(
    // Update state based on URL parameter (on/off) only in preview flow,
    checkCampaignStatus(campaignStartDate, campaignEndDate, cycleState, isPreview)
  );

  const GetMockData = async (isAuthorMode: boolean) => {
    if (shouldUseMockData(isAuthorMode)) {
      const mockData = await import('./__mock__/OnOffContainerMock.mock');
      return mockData.offersDataMock;
    }
    return {};
  };

  useEffect(() => {
    const fetchMockData = async () => {
      const mockData = await GetMockData(isAuthorMode);
      setoffersDataMock(mockData);
    };

    fetchMockData();
  }, [isAuthorMode]);

  useEffect(() => {
    if (isAuthorMode) return;
    const updateTime = () => {
      const newTimeLeft = calculateTimeLeft(campaignStartDate, isAuthorMode);

      if (newTimeLeft.days === 0 && newTimeLeft.hours === 0 && newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
        setUtilityState(checkCampaignStatus(campaignStartDate, campaignEndDate, cycleState, isPreview));
      }
    };
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [isAuthorMode, campaignStartDate, campaignEndDate]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let timeoutId: NodeJS.Timeout;

    function updateMargin() {
      const previewTextElement = document.querySelector('[data-component-name="o-offers-text"]');
      const searchForm = document.querySelector('[data-component-name="o-shop-searchform"]');
      const searchFormChild = searchForm?.querySelector('.document_search_form_container_el_ref');
      const topInPx = searchFormChild
        ? parseFloat(window.getComputedStyle(searchFormChild as HTMLElement).top) || 0
        : 0;

      if (isPreview === 'true' && previewTextElement && searchForm) {
        const previewElementbottom = (previewTextElement as HTMLElement).getBoundingClientRect().bottom;
        (searchForm as HTMLElement).style.marginTop = `${previewElementbottom - topInPx}px`;
      }
    }

    function handleResize() {
      clearTimeout(timeoutId);
      // Debounce for layout stability during rapid resizing
      timeoutId = setTimeout(updateMargin, 500);
    }

    /* Initial run with delay.
     Needed because the search form loads after a short delay.
     This ensures margin is applied once it's fully mounted.
    */

    timeoutId = setTimeout(updateMargin, 1200);

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (variation === utilityState || isAuthorMode) {
    return <ResponsiveGrid isInEditor={true} customClassName={clsx(model?.appliedCssClassNames)} {...props} />;
  }

  return <></>;
};

export const OnOffContainerWrapperConfig = {
  emptyLabel: 'OnOffContainer',
  isEmpty: false,
  resourceType: `mi-aem-offers-spa/components/onoffcontainer`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OnOffContainer: FC<OnOffContainerProps> = (props: any) => {
  return (
    <EditableComponent config={OnOffContainerWrapperConfig} {...props}>
      <div data-testid="offers-OnOffContainer" data-component-name="o-offers-OnOffContainer">
        <OnOffContainerWrapper {...props} />
      </div>
    </EditableComponent>
  );
};
